import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateActiveShopperId } from 'actions';
import { Shopper, OrderObject, ResponsiblePerson } from 'common/types';
import { Theme, Typography, Box } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { RiUserLine, RiUserFill, RiAddCircleLine } from 'react-icons/ri';
import AddPerson from './AddPerson';
import * as RentalViewSelectors from 'selectors/RentalViewSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import { sortShoppers } from 'common/utils/rentalUtils';
import { useTranslation } from 'services/localization/useTranslation';
import { orderHasDeliveryOrPickup } from 'common/modules/delivery/utils';
import { usePermissions } from 'hooks/usePermissions';

interface Props {
	rental: OrderObject;
	shoppers: Shopper[];
	responsiblePerson: ResponsiblePerson;
	scrollToShopper: (shopperId: string) => void;
}

const ParticipantsBar = (props: Props) => {
	const { rental, shoppers, scrollToShopper, responsiblePerson } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const activeShopperId = useSelector(RentalViewSelectors.activeShopperId);
	const [addPersonDialogOpen, setAddPersonDialogOpen] = useState(false);
	const [newShopperId, setNewShopperId] = useState('');
	const newShopper = shoppers.find((s) => s.id === newShopperId);
	const { t } = useTranslation();
	const { hasPermission } = usePermissions();
	const sortedShoppers = sortShoppers(shoppers, responsiblePerson);

	const rentalHasDelivery = orderHasDeliveryOrPickup(rental.orderDelivery);

	useEffect(() => {
		if (newShopper) {
			handleParticipantClick(newShopperId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [newShopperId]);

	const handleParticipantClick = (shopperId: string) => {
		dispatch(updateActiveShopperId(shopperId));
		scrollToShopper(shopperId);
	};

	const handleAddShopperDialogClose = (success: boolean, newShopperId?: string) => {
		setAddPersonDialogOpen(false);
		if (success) {
			dispatch(
				NotificationActions.showNotification({
					message: t('personAdded', 'Person added'),
					variant: 'info',
				}),
			);
		}
		if (newShopperId) {
			setNewShopperId(newShopperId);
		}
	};

	return (
		<>
			<Grid container>
				<Grid container item className={classes.participantsList}>
					{sortedShoppers.map((shopper) => (
						<Grid item key={shopper.id}>
							<Button
								size="small"
								onClick={() => handleParticipantClick(shopper.id)}
								variant="text"
								className={classes.participantButton}
							>
								{shopper.id === activeShopperId ? (
									<RiUserFill size={20} />
								) : (
									<RiUserLine size={20} />
								)}
								<Typography variant="body2" className={classes.shopperName} data-sl="mask">
									{shopper.firstName}
								</Typography>
							</Button>
						</Grid>
					))}
				</Grid>
				{rental.rentalInfo.rentalState !== 'COMPLETED' && !rentalHasDelivery && (
					<Grid item>
						<Button
							size="small"
							onClick={() => setAddPersonDialogOpen(true)}
							className={classes.participantButton}
							disabled={!hasPermission('edit_booking')}
						>
							<RiAddCircleLine size={20} />
							<Box height={20} />
						</Button>
					</Grid>
				)}
			</Grid>
			{addPersonDialogOpen && (
				<AddPerson
					open={addPersonDialogOpen}
					activeRental={rental}
					handleClose={handleAddShopperDialogClose}
				/>
			)}
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		participantsList: {
			flexWrap: 'nowrap',
			overflowX: 'auto',
			overflowY: 'hidden',
			flex: '1',
		},
		box: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			cursor: 'pointer',
			whiteSpace: 'nowrap',
		},
		shopperName: {
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			maxWidth: 70, // This is to include ~9 letter names still, but ... longer
			textOverflow: 'ellipsis',
		},
		participantButton: {
			borderRadius: 4,
			minWidth: 0,
			textTransform: 'none',
			color: theme.palette.common.black,
		},
		participantButtonLabel: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			whiteSpace: 'nowrap',
		},
	}),
);

export default ParticipantsBar;
